import React from "react";
import { useRef } from "react";

import { useState, useEffect, useLayoutEffect } from "react";
import { inputStyle } from "./formConstants";

// Helper function to create required field labels with red asterisks
export const createRequiredLabel = (text) => {
  return (
    <span className="form-label">
      {text}: <span style={{ color: "red", fontWeight: "bold" }}>*</span>
    </span>
  );
};

export const FormInput = ({
  label,
  id,
  name,
  value,
  onChange,
  required = false,
  placeholder = "",
  maxLength = "", // ✅ optional parameter for text length
}) => {
  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  useLayoutEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== null) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
    }
  });

  const handleChange = (e) => {
    cursorPositionRef.current = e.target.selectionStart;
    onChange(e);
  };

  return (
    <div>
      <label htmlFor={id} style={{ marginBottom: 6 }}>
        {label}
      </label>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={value || ""}
        onChange={handleChange}
        style={inputStyle}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength} // ✅ applies the optional limit
      />
    </div>
  );
};

export const FormCheckBox = ({ label, name, checked, onChange }) => {
  return (
    <div style={{ display: "flex", flexDirection: "row", marginBottom: 6 }}>
      <div style={{ marginTop: 2 }}>
        <input id={name} name={name} type="checkbox" checked={checked} onChange={onChange} />
      </div>
      <div style={{ marginLeft: 8, fontSize: 14 }}>
        <label htmlFor={name} className="font-medium text-gray-700">
          {label}
        </label>
      </div>
    </div>
  );
};

export const FormTitle = ({ value }) => {
  return <h6 className="text-xl font-semibold mb-4 border-b pb-2">{value}</h6>;
};
export const FormsubTitle = ({ value }) => {
  return (
    <label htmlFor={value} style={{ marginBottom: 6 }}>
      {value}
    </label>
  );
};

export const FormDate = ({ label, name, value, onChange }) => {
  const hiddenDateInputRef = useRef(null);
  const [validationMessage, setValidationMessage] = useState("");

  const convertToYYYYMMDD = (dateStr) => {
    if (!dateStr) return "";

    // Trim any spaces from the date string
    const cleanDate = dateStr.trim();

    // Handle MM/DD/YYYY format (like "03/17/1985")
    if (cleanDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const [month, day, year] = cleanDate.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // Handle MM-DD-YYYY format (like "01-17-1985")
    if (cleanDate.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const [month, day, year] = cleanDate.split("-");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // Handle DD/MM/YYYY format
    const parts = cleanDate.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // If already in YYYY-MM-DD format, return as is
    if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return cleanDate;
    }

    return "";
  };

  const formatToMMDDYYYY = (dateStr) => {
    if (!dateStr) return "";

    // Trim any spaces from the date string
    const cleanDate = dateStr.trim();

    // If already in MM/DD/YYYY format, return as is
    if (cleanDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      return cleanDate;
    }

    // Convert from YYYY-MM-DD to MM/DD/YYYY
    if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = cleanDate.split("-");
      return `${month}/${day}/${year}`;
    }

    // Handle MM-DD-YYYY format
    if (cleanDate.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const [month, day, year] = cleanDate.split("-");
      return `${month}/${day}/${year}`;
    }

    return cleanDate;
  };

  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    // Days in each month (non-leap year)
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Check for leap year for February
    if (monthNum === 2) {
      const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0;
      return isLeapYear ? 29 : 28;
    }

    return daysInMonth[monthNum - 1] || 31;
  };

  // Helper function to convert 2-digit year to 4-digit year
  const convertTwoDigitYear = (twoDigitYear) => {
    const year = parseInt(twoDigitYear);
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;

    // If year is less than or equal to current year's last 2 digits, assume current century
    // Otherwise, assume previous century
    if (year <= currentYear % 100) {
      return currentCentury + year;
    } else {
      return currentCentury - 100 + year;
    }
  };

  // Validate date format and show warning messages
  const validateDateFormat = (dateStr) => {
    if (!dateStr || dateStr.trim() === "") {
      setValidationMessage("");
      return true;
    }

    const cleanDate = dateStr.trim();

    // Check for valid formats
    const validFormats = [
      /^\d{1,2}\/\d{1,2}\/\d{4}$/, // M/D/YYYY, MM/DD/YYYY, M/DD/YYYY, MM/D/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{2}$/, // MM/DD/YY
      /^\d{2}-\d{2}-\d{4}$/, // MM-DD-YYYY
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    ];

    const isValidFormat = validFormats.some((pattern) => pattern.test(cleanDate));

    if (!isValidFormat) {
      setValidationMessage(
        "⚠️ Invalid date format. Please use MM/DD/YYYY format (e.g., 12/25/2024)"
      );
      return false;
    }

    // Clear validation message when format is valid (will be set again if date values are invalid)
    setValidationMessage("");

    // Additional validation for date values
    let month, day, year;

    if (cleanDate.match(/^\d{1,2}\/\d{1,2}\/\d{2,4}$/)) {
      const parts = cleanDate.split("/");
      month = parseInt(parts[0]);
      day = parseInt(parts[1]);
      year = parts[2].length === 2 ? convertTwoDigitYear(parts[2]) : parseInt(parts[2]);
    } else if (cleanDate.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const parts = cleanDate.split("-");
      month = parseInt(parts[0]);
      day = parseInt(parts[1]);
      year = parseInt(parts[2]);
    } else if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = cleanDate.split("-");
      year = parseInt(parts[0]);
      month = parseInt(parts[1]);
      day = parseInt(parts[2]);
    }

    // Validate month (1-12)
    if (month < 1 || month > 12) {
      setValidationMessage("⚠️ Invalid month. Month must be between 01 and 12");
      return false;
    }

    // Validate day based on month
    const maxDays = getDaysInMonth(month, year);
    if (day < 1 || day > maxDays) {
      setValidationMessage(
        `⚠️ Invalid day. Day must be between 01 and ${maxDays} for month ${month}`
      );
      return false;
    }

    // Validate year range
    if (year < 1800 || year > 3000) {
      setValidationMessage("⚠️ Invalid year. Year must be between 1800 and 3000");
      return false;
    }

    // Check if it's a valid date
    const testDate = new Date(year, month - 1, day);
    if (
      testDate.getFullYear() !== year ||
      testDate.getMonth() !== month - 1 ||
      testDate.getDate() !== day
    ) {
      setValidationMessage("⚠️ Invalid date. Please check the date values");
      return false;
    }

    // setValidationMessage("✓ Valid date format");
    return true;
  };

  // Validate initial value on component mount
  useEffect(() => {
    if (value) {
      validateDateFormat(value);
    }
  }, [value]);

  const handleTextInputChange = (e) => {
    let inputValue = e.target.value;

    // Remove any non-digit characters except /
    inputValue = inputValue.replace(/[^\d\/]/g, "");

    // Split the input to validate each part
    const parts = inputValue.split("/");
    let month = parts[0] || "";
    let day = parts[1] || "";
    let year = parts[2] || "";

    // Validate and restrict month (01-12)
    if (month.length === 1) {
      if (parseInt(month) > 1) {
        month = "0" + month;
      }
    } else if (month.length === 2) {
      const monthNum = parseInt(month);
      if (monthNum > 12) {
        month = "12";
      } else if (monthNum === 0) {
        month = "01";
      }
    }

    // Validate and restrict day based on month
    if (day.length >= 1 && month.length === 2) {
      const monthNum = parseInt(month);
      const maxDays = getDaysInMonth(monthNum, year || new Date().getFullYear());

      if (day.length === 1) {
        const dayNum = parseInt(day);
        if (dayNum > 3 && maxDays < 30) {
          day = "0" + day;
        } else if (dayNum > Math.floor(maxDays / 10)) {
          day = "0" + day;
        }
      } else if (day.length === 2) {
        const dayNum = parseInt(day);
        if (dayNum > maxDays) {
          day = maxDays.toString().padStart(2, "0");
        } else if (dayNum === 0) {
          day = "01";
        }
      }
    }

    // Validate and restrict year (1800-3000)
    if (year.length >= 4) {
      const yearNum = parseInt(year);
      if (yearNum < 1800) {
        year = "1800";
      } else if (yearNum > 3000) {
        year = "3000";
      }

      // Re-validate day if year changed (for leap year check)
      if (day.length === 2 && month.length === 2) {
        const monthNum = parseInt(month);
        const maxDays = getDaysInMonth(monthNum, yearNum);
        const dayNum = parseInt(day);
        if (dayNum > maxDays) {
          day = maxDays.toString().padStart(2, "0");
        }
      }
    }

    // Reconstruct the input value
    if (month.length === 2 && day.length === 0) {
      inputValue = month + "/";
    } else if (month.length === 2 && day.length >= 1 && day.length < 2) {
      inputValue = month + "/" + day;
    } else if (month.length === 2 && day.length === 2 && year.length === 0) {
      inputValue = month + "/" + day + "/";
    } else if (month.length === 2 && day.length === 2 && year.length >= 1) {
      inputValue = month + "/" + day + "/" + year;
    } else {
      inputValue = month + (day ? "/" + day : "") + (year ? "/" + year : "");
    }

    // Auto-format as user types MM/DD/YYYY
    if (inputValue.length === 2 && !inputValue.includes("/")) {
      inputValue = inputValue + "/";
    } else if (inputValue.length === 5 && inputValue.split("/").length === 2) {
      inputValue = inputValue + "/";
    }

    // Limit to MM/DD/YYYY format (10 characters)
    if (inputValue.length > 10) {
      inputValue = inputValue.substring(0, 10);
    }

    onChange({
      target: {
        name,
        value: inputValue,
      },
    });

    // Validate the date format and show warnings
    validateDateFormat(inputValue);
  };

  const handleCalendarIconClick = () => {
    if (hiddenDateInputRef.current) {
      hiddenDateInputRef.current.showPicker();
    }
  };

  const handleHiddenDateChange = (e) => {
    const selectedDate = e.target.value; // YYYY-MM-DD format
    const formattedDate = formatToMMDDYYYY(selectedDate);

    onChange({
      target: {
        name,
        value: formattedDate,
      },
    });

    // Validate the formatted date
    validateDateFormat(formattedDate);
  };

  // Convert display value to MM/DD/YYYY format
  const displayValue = formatToMMDDYYYY(value);

  // Convert value to YYYY-MM-DD for hidden date input
  const hiddenInputValue = convertToYYYYMMDD(value);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <label htmlFor={name} style={{ marginBottom: 6, color: "#6A6A6B" }}>
        {label}
      </label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          type="text"
          id={name}
          name={name}
          value={displayValue || ""}
          onChange={handleTextInputChange}
          style={{
            ...inputStyle,
            marginBottom: 0,
            paddingRight: "40px", // Make room for calendar icon
          }}
          placeholder="MM/DD/YYYY"
        />

        {/* Calendar Icon */}
        <div
          onClick={handleCalendarIconClick}
          style={{
            position: "absolute",
            right: "12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            color: "#6b7280",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>

        {/* Hidden date input for calendar picker */}
        <input
          ref={hiddenDateInputRef}
          type="date"
          value={hiddenInputValue || ""}
          onChange={handleHiddenDateChange}
          min="1800-01-01"
          max="3000-12-31"
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            width: 0,
            height: 0,
          }}
        />
      </div>

      {/* Validation Message */}
      {validationMessage && (
        <div
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: validationMessage.includes("⚠️")
              ? "#dc2626"
              : validationMessage.includes("✓")
                ? "#16a34a"
                : "#6b7280",
            fontWeight: "500",
          }}
        >
          {validationMessage}
        </div>
      )}
    </div>
  );
};

export const NormalFormDate = ({ label, name, value, onChange }) => {
  const hiddenDateInputRef = useRef(null);
  const textInputRef = useRef(null);
  const [validationMessage, setValidationMessage] = useState("");

  const convertToYYYYMMDD = (dateStr) => {
    if (!dateStr) return "";
    const cleanDate = dateStr.trim();

    // Handle DD/MM/YYYY
    if (cleanDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const [day, month, year] = cleanDate.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // Handle DD-MM-YYYY
    if (cleanDate.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
      const [day, month, year] = cleanDate.split("-");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // Already in YYYY-MM-DD
    if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return cleanDate;
    }

    return "";
  };

  const formatToDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const cleanDate = dateStr.trim();

    // Already in DD/MM/YYYY
    if (cleanDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      return cleanDate;
    }

    // Convert YYYY-MM-DD → DD/MM/YYYY
    if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = cleanDate.split("-");
      return `${day}/${month}/${year}`;
    }

    // Convert DD-MM-YYYY → DD/MM/YYYY
    if (cleanDate.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
      const [day, month, year] = cleanDate.split("-");
      return `${day}/${month}/${year}`;
    }

    return cleanDate;
  };

  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (monthNum === 2) {
      const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0;
      return isLeapYear ? 29 : 28;
    }

    return daysInMonth[monthNum - 1] || 31;
  };

  const convertTwoDigitYear = (twoDigitYear) => {
    const year = parseInt(twoDigitYear);
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    return year <= currentYear % 100 ? currentCentury + year : currentCentury - 100 + year;
  };

  const validateDateFormat = (dateStr) => {
    if (!dateStr || dateStr.trim() === "") {
      setValidationMessage("");
      return true;
    }

    const cleanDate = dateStr.trim();
    const validFormats = [
      /^\d{1,2}\/\d{1,2}\/\d{4}$/, // D/M/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{2}$/, // D/M/YY
      /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
    ];

    const isValidFormat = validFormats.some((p) => p.test(cleanDate));
    if (!isValidFormat) {
      setValidationMessage("⚠️ Invalid date format. Please use DD/MM/YYYY (e.g., 25/12/2024)");
      return false;
    }

    // Clear validation message when format is valid (will be set again if date values are invalid)
    setValidationMessage("");

    let day, month, year;

    if (cleanDate.match(/^\d{1,2}\/\d{1,2}\/\d{2,4}$/)) {
      const parts = cleanDate.split("/");
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parts[2].length === 2 ? convertTwoDigitYear(parts[2]) : parseInt(parts[2]);
    } else if (cleanDate.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const parts = cleanDate.split("-");
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parseInt(parts[2]);
    } else if (cleanDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parts = cleanDate.split("-");
      year = parseInt(parts[0]);
      month = parseInt(parts[1]);
      day = parseInt(parts[2]);
    }

    if (month < 1 || month > 12) {
      setValidationMessage("⚠️ Invalid month. Must be between 01 and 12");
      return false;
    }

    const maxDays = getDaysInMonth(month, year);
    if (day < 1 || day > maxDays) {
      setValidationMessage(`⚠️ Invalid day. Must be between 01 and ${maxDays}`);
      return false;
    }

    if (year < 1800 || year > 3000) {
      setValidationMessage("⚠️ Invalid year. Must be between 1800 and 3000");
      return false;
    }

    const testDate = new Date(year, month - 1, day);
    if (
      testDate.getFullYear() !== year ||
      testDate.getMonth() !== month - 1 ||
      testDate.getDate() !== day
    ) {
      setValidationMessage("⚠️ Invalid date values");
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (value) validateDateFormat(value);
  }, [value]);

  const handleTextInputChange = (e) => {
    const rawValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    const previousValue = formatToDDMMYYYY(value) || "";

    // Only allow digits and forward slashes
    let inputValue = rawValue.replace(/[^\d\/]/g, "");

    // Handle backspace/deletion by checking if input is shorter
    const isDeleting = inputValue.length < previousValue.replace(/[^\d\/]/g, "").length;

    // If deleting, be more permissive and don't auto-format aggressively
    if (isDeleting) {
      onChange({
        target: {
          name,
          value: inputValue,
        },
      });
      validateDateFormat(inputValue);
      return;
    }

    const parts = inputValue.split("/");
    let day = parts[0] || "";
    let month = parts[1] || "";
    let year = parts[2] || "";

    // Handle day input
    if (day.length > 0) {
      // Limit day to 2 characters first
      if (day.length > 2) {
        day = day.slice(0, 2);
      }

      // Auto-format single digits 4-9 to 04-09 for day
      if (day.length === 1 && !parts[1] && !isDeleting) {
        const dayNum = parseInt(day);
        if (dayNum >= 4 && dayNum <= 9) {
          day = "0" + day;
        }
      }

      // Validate day range
      if (day.length === 2) {
        const dayNum = parseInt(day);
        if (dayNum > 31) {
          day = "31";
        } else if (dayNum === 0) {
          day = "01";
        }
      }
    }

    // Handle month input
    if (month.length > 0) {
      // Limit month to 2 characters
      if (month.length > 2) {
        month = month.slice(0, 2);
      }

      // Auto-format single digits 2-9 to 02-09 for month
      if (month.length === 1 && !parts[2] && !isDeleting) {
        const monthNum = parseInt(month);
        if (monthNum >= 2 && monthNum <= 9) {
          month = "0" + month;
        }
      }

      // Validate month range
      if (month.length === 2) {
        const monthNum = parseInt(month);
        if (monthNum > 12) {
          month = "12";
        } else if (monthNum === 0) {
          month = "01";
        }
      }
    }

    // Handle year input - just limit to 4 digits
    if (year.length > 4) {
      year = year.slice(0, 4);
    }

    // Build the formatted value
    let formattedValue = "";

    if (day) {
      formattedValue = day;

      // Auto-add slash after day when it's complete
      if (day.length === 2 && !month && rawValue.length === 2) {
        formattedValue += "/";
      }
    }

    if (month || parts.length > 1) {
      formattedValue += (formattedValue && !formattedValue.endsWith("/") ? "/" : "") + month;

      // Auto-add slash after month when it's complete
      if (day.length === 2 && month.length === 2 && !year && rawValue.length === 5) {
        formattedValue += "/";
      }
    }

    if (year || parts.length > 2) {
      formattedValue += (formattedValue && !formattedValue.endsWith("/") ? "/" : "") + year;
    }

    // Final validation - ensure day doesn't exceed month's maximum
    if (day.length === 2 && month.length >= 1) {
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);

      if (monthNum >= 1 && monthNum <= 12) {
        const maxDays = getDaysInMonth(monthNum, year ? parseInt(year) : new Date().getFullYear());
        if (dayNum > maxDays) {
          const newDay = maxDays.toString().padStart(2, "0");
          formattedValue = formattedValue.replace(day, newDay);
        }
      }
    }

    // Store the cursor position for restoration
    const newCursorPos = cursorPosition;

    onChange({
      target: {
        name,
        value: formattedValue,
      },
    });

    validateDateFormat(formattedValue);

    // Restore cursor position after state update
    setTimeout(() => {
      if (textInputRef.current) {
        // Adjust cursor position if value was auto-formatted
        let adjustedPos = newCursorPos;
        if (formattedValue.length > rawValue.length) {
          adjustedPos = Math.min(newCursorPos + 1, formattedValue.length);
        }
        textInputRef.current.setSelectionRange(adjustedPos, adjustedPos);
      }
    }, 0);
  };

  const handleCalendarIconClick = () => {
    if (hiddenDateInputRef.current) hiddenDateInputRef.current.showPicker();
  };

  const handleHiddenDateChange = (e) => {
    const formattedDate = formatToDDMMYYYY(e.target.value);
    onChange({
      target: {
        name,
        value: formattedDate,
      },
    });
    validateDateFormat(formattedDate);
  };

  const displayValue = formatToDDMMYYYY(value);
  const hiddenInputValue = convertToYYYYMMDD(value);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <label htmlFor={name} style={{ marginBottom: 6, color: "#6A6A6B" }}>
        {label}
      </label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          ref={textInputRef}
          type="text"
          id={name}
          name={name}
          value={displayValue || ""}
          onChange={handleTextInputChange}
          style={{
            ...inputStyle,
            marginBottom: 0,
            paddingRight: "40px",
          }}
          placeholder="DD/MM/YYYY"
        />

        {/* Calendar Icon */}
        <div
          onClick={handleCalendarIconClick}
          style={{
            position: "absolute",
            right: "12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            color: "#6b7280",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>

        {/* Hidden date input for calendar picker */}
        <input
          ref={hiddenDateInputRef}
          type="date"
          value={hiddenInputValue || ""}
          onChange={handleHiddenDateChange}
          min="1800-01-01"
          max="3000-12-31"
          lang="en-GB"
          style={{
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            width: 0,
            height: 0,
          }}
        />
      </div>

      {validationMessage && (
        <div
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: validationMessage.includes("⚠️")
              ? "#dc2626"
              : validationMessage.includes("✓")
                ? "#16a34a"
                : "#6b7280",
            fontWeight: "500",
          }}
        >
          {validationMessage}
        </div>
      )}
    </div>
  );
};
